import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ArrowLeft, Globe, RotateCcw, Play, Pause, Info, 
  Plus, Minus, Zap, Eye, Sun, Moon, Lightbulb,
  Move, Trash2, Settings, ChevronRight
} from 'lucide-react';

/**
 * THE LIGHT BENDER - World's Best Optics Simulation
 * Interactive 3D-like environment for light, mirrors, lenses
 * Students can build telescopes, microscopes, periscopes
 */
const LightBenderSimulation = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [language, setLanguage] = useState('en');
  const [isPlaying, setIsPlaying] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const [selectedTool, setSelectedTool] = useState('ray');
  const [objects, setObjects] = useState([]);
  const [rays, setRays] = useState([]);
  const [draggedObject, setDraggedObject] = useState(null);
  const [preset, setPreset] = useState(null);

  // Text translations
  const t = {
    title: language === 'en' ? 'The Light Bender' : 'प्रकाश मोड़क',
    subtitle: language === 'en' ? 'Build optical systems like telescopes & microscopes' : 'दूरबीन और सूक्ष्मदर्शी जैसी प्रकाशिक प्रणालियाँ बनाएं',
    tools: language === 'en' ? 'Tools' : 'उपकरण',
    ray: language === 'en' ? 'Light Ray' : 'प्रकाश किरण',
    convexLens: language === 'en' ? 'Convex Lens' : 'उत्तल लेंस',
    concaveLens: language === 'en' ? 'Concave Lens' : 'अवतल लेंस',
    convexMirror: language === 'en' ? 'Convex Mirror' : 'उत्तल दर्पण',
    concaveMirror: language === 'en' ? 'Concave Mirror' : 'अवतल दर्पण',
    planeMirror: language === 'en' ? 'Plane Mirror' : 'समतल दर्पण',
    prism: language === 'en' ? 'Prism' : 'प्रिज्म',
    screen: language === 'en' ? 'Screen' : 'पर्दा',
    presets: language === 'en' ? 'Quick Builds' : 'त्वरित निर्माण',
    telescope: language === 'en' ? 'Telescope' : 'दूरबीन',
    microscope: language === 'en' ? 'Microscope' : 'सूक्ष्मदर्शी',
    periscope: language === 'en' ? 'Periscope' : 'पेरिस्कोप',
    rainbow: language === 'en' ? 'Rainbow (Prism)' : 'इंद्रधनुष (प्रिज्म)',
    clear: language === 'en' ? 'Clear All' : 'सब हटाएं',
    info: language === 'en' ? 'How it works' : 'यह कैसे काम करता है',
    infoText: language === 'en' 
      ? 'Drag optical elements onto the canvas. Light rays will automatically bend according to the laws of reflection and refraction. Build complex systems to understand how telescopes magnify distant objects or how microscopes reveal tiny worlds!'
      : 'प्रकाशिक तत्वों को कैनवास पर खींचें। प्रकाश किरणें स्वचालित रूप से परावर्तन और अपवर्तन के नियमों के अनुसार मुड़ जाएंगी। दूरबीन कैसे दूर की वस्तुओं को बड़ा करती है या सूक्ष्मदर्शी कैसे छोटी दुनिया दिखाती है, समझने के लिए जटिल प्रणालियाँ बनाएं!',
    focalLength: language === 'en' ? 'Focal Length' : 'फोकस दूरी',
    dragToMove: language === 'en' ? 'Drag to move' : 'खींचकर हिलाएं',
  };

  // Optical tools with properties
  const tools = [
    { id: 'ray', name: t.ray, icon: '→', color: '#fbbf24' },
    { id: 'convexLens', name: t.convexLens, icon: ')(', color: '#3b82f6', focalLength: 80 },
    { id: 'concaveLens', name: t.concaveLens, icon: '()', color: '#8b5cf6', focalLength: -80 },
    { id: 'convexMirror', name: t.convexMirror, icon: ')', color: '#10b981', focalLength: -60 },
    { id: 'concaveMirror', name: t.concaveMirror, icon: '(', color: '#f43f5e', focalLength: 60 },
    { id: 'planeMirror', name: t.planeMirror, icon: '|', color: '#6b7280' },
    { id: 'prism', name: t.prism, icon: '△', color: '#06b6d4' },
    { id: 'screen', name: t.screen, icon: '▮', color: '#374151' },
  ];

  // Preset configurations
  const presets = [
    { id: 'telescope', name: t.telescope, icon: '🔭' },
    { id: 'microscope', name: t.microscope, icon: '🔬' },
    { id: 'periscope', name: t.periscope, icon: '📡' },
    { id: 'rainbow', name: t.rainbow, icon: '🌈' },
  ];

  // Load preset configuration
  const loadPreset = (presetId) => {
    setObjects([]);
    setRays([]);
    
    switch(presetId) {
      case 'telescope':
        setObjects([
          { id: 1, type: 'convexLens', x: 200, y: 250, focalLength: 120, label: language === 'en' ? 'Objective Lens' : 'वस्तु लेंस' },
          { id: 2, type: 'convexLens', x: 500, y: 250, focalLength: 40, label: language === 'en' ? 'Eyepiece' : 'नेत्रिका' },
        ]);
        setRays([
          { id: 1, startX: 50, startY: 220, angle: 5 },
          { id: 2, startX: 50, startY: 250, angle: 0 },
          { id: 3, startX: 50, startY: 280, angle: -5 },
        ]);
        break;
      case 'microscope':
        setObjects([
          { id: 1, type: 'convexLens', x: 300, y: 350, focalLength: 30, label: language === 'en' ? 'Objective' : 'वस्तुका' },
          { id: 2, type: 'convexLens', x: 300, y: 150, focalLength: 60, label: language === 'en' ? 'Eyepiece' : 'नेत्रिका' },
        ]);
        setRays([
          { id: 1, startX: 300, startY: 450, angle: -85 },
          { id: 2, startX: 320, startY: 450, angle: -88 },
          { id: 3, startX: 280, startY: 450, angle: -92 },
        ]);
        break;
      case 'periscope':
        setObjects([
          { id: 1, type: 'planeMirror', x: 200, y: 150, angle: 45 },
          { id: 2, type: 'planeMirror', x: 200, y: 350, angle: 45 },
        ]);
        setRays([
          { id: 1, startX: 50, startY: 150, angle: 0 },
        ]);
        break;
      case 'rainbow':
        setObjects([
          { id: 1, type: 'prism', x: 350, y: 250 },
        ]);
        setRays([
          { id: 1, startX: 50, startY: 250, angle: 0, isWhite: true },
        ]);
        break;
    }
    setPreset(presetId);
  };

  // Add object to canvas
  const addObject = (toolId) => {
    if (toolId === 'ray') {
      const newRay = {
        id: Date.now(),
        startX: 50,
        startY: 200 + Math.random() * 100,
        angle: 0
      };
      setRays([...rays, newRay]);
    } else {
      const tool = tools.find(t => t.id === toolId);
      const newObject = {
        id: Date.now(),
        type: toolId,
        x: 300 + Math.random() * 200,
        y: 200 + Math.random() * 100,
        focalLength: tool?.focalLength || 80,
        angle: 0
      };
      setObjects([...objects, newObject]);
    }
  };

  // Handle canvas click/drag
  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Check if clicking on an object
    const clickedObj = objects.find(obj => 
      Math.abs(obj.x - x) < 40 && Math.abs(obj.y - y) < 40
    );
    
    if (clickedObj) {
      setDraggedObject({ ...clickedObj, offsetX: x - clickedObj.x, offsetY: y - clickedObj.y });
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!draggedObject || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - draggedObject.offsetX;
    const y = e.clientY - rect.top - draggedObject.offsetY;
    
    setObjects(objects.map(obj => 
      obj.id === draggedObject.id ? { ...obj, x, y } : obj
    ));
  };

  const handleCanvasMouseUp = () => {
    setDraggedObject(null);
  };

  // Draw simulation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear canvas
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, width, height);
    
    // Draw grid
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1;
    for (let x = 0; x < width; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
    
    // Draw optical axis
    ctx.strokeStyle = '#475569';
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(0, height/2);
    ctx.lineTo(width, height/2);
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Draw objects
    objects.forEach(obj => {
      ctx.save();
      ctx.translate(obj.x, obj.y);
      
      switch(obj.type) {
        case 'convexLens':
          // Draw convex lens shape
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.ellipse(0, 0, 8, 60, 0, 0, Math.PI * 2);
          ctx.stroke();
          ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
          ctx.fill();
          // Draw focal points
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(-obj.focalLength, 0, 4, 0, Math.PI * 2);
          ctx.arc(obj.focalLength, 0, 4, 0, Math.PI * 2);
          ctx.fill();
          break;
          
        case 'concaveLens':
          ctx.strokeStyle = '#8b5cf6';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(-4, -60);
          ctx.quadraticCurveTo(8, 0, -4, 60);
          ctx.moveTo(4, -60);
          ctx.quadraticCurveTo(-8, 0, 4, 60);
          ctx.stroke();
          break;
          
        case 'convexMirror':
          ctx.strokeStyle = '#10b981';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(-30, 0, 50, -Math.PI/3, Math.PI/3);
          ctx.stroke();
          // Reflective coating
          ctx.strokeStyle = '#6ee7b7';
          ctx.lineWidth = 2;
          ctx.stroke();
          break;
          
        case 'concaveMirror':
          ctx.strokeStyle = '#f43f5e';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.arc(30, 0, 50, Math.PI - Math.PI/3, Math.PI + Math.PI/3);
          ctx.stroke();
          // Focus point
          ctx.fillStyle = '#fbbf24';
          ctx.beginPath();
          ctx.arc(-obj.focalLength, 0, 4, 0, Math.PI * 2);
          ctx.fill();
          break;
          
        case 'planeMirror':
          ctx.strokeStyle = '#9ca3af';
          ctx.lineWidth = 4;
          ctx.save();
          ctx.rotate((obj.angle || 0) * Math.PI / 180);
          ctx.beginPath();
          ctx.moveTo(0, -50);
          ctx.lineTo(0, 50);
          ctx.stroke();
          // Mirror backing
          ctx.fillStyle = '#374151';
          ctx.fillRect(2, -50, 6, 100);
          ctx.restore();
          break;
          
        case 'prism':
          ctx.strokeStyle = '#06b6d4';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(0, -50);
          ctx.lineTo(-43, 50);
          ctx.lineTo(43, 50);
          ctx.closePath();
          ctx.stroke();
          ctx.fillStyle = 'rgba(6, 182, 212, 0.2)';
          ctx.fill();
          break;
          
        case 'screen':
          ctx.fillStyle = '#e5e7eb';
          ctx.fillRect(-5, -60, 10, 120);
          break;
      }
      
      // Draw label
      if (obj.label) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(obj.label, 0, 80);
      }
      
      ctx.restore();
    });
    
    // Draw light rays with refraction/reflection
    rays.forEach(ray => {
      let currentX = ray.startX;
      let currentY = ray.startY;
      let currentAngle = ray.angle;
      
      const colors = ray.isWhite ? ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'] : ['#fbbf24'];
      
      colors.forEach((color, colorIdx) => {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.shadowColor = color;
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.moveTo(currentX, currentY);
        
        let x = currentX;
        let y = currentY;
        let angle = currentAngle + (ray.isWhite ? (colorIdx - 3) * 3 : 0);
        
        // Trace ray through objects
        for (let step = 0; step < 1000; step += 2) {
          x += Math.cos(angle * Math.PI / 180) * 2;
          y += Math.sin(angle * Math.PI / 180) * 2;
          
          if (x < 0 || x > width || y < 0 || y > height) break;
          
          // Check intersection with objects
          objects.forEach(obj => {
            const dx = x - obj.x;
            const dy = y - obj.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (obj.type === 'convexLens' && Math.abs(dx) < 10 && Math.abs(dy) < 60) {
              // Refraction through convex lens
              const relY = dy / 60;
              angle = angle - relY * 20 * (80 / obj.focalLength);
            }
            else if (obj.type === 'concaveLens' && Math.abs(dx) < 10 && Math.abs(dy) < 60) {
              // Diverging lens
              const relY = dy / 60;
              angle = angle + relY * 15;
            }
            else if (obj.type === 'concaveMirror' && dist < 55 && dist > 45 && dx < 0) {
              // Reflection from concave mirror
              angle = 180 - angle + (dy / 50) * 30;
            }
            else if (obj.type === 'planeMirror' && Math.abs(dx) < 8 && Math.abs(dy) < 50) {
              // Reflection from plane mirror
              angle = 180 - angle;
            }
            else if (obj.type === 'prism' && Math.abs(dx) < 45 && dy > -50 && dy < 50) {
              // Dispersion through prism
              if (dx < 0) angle = angle + 15;
              else angle = angle + 25;
            }
          });
          
          ctx.lineTo(x, y);
        }
        
        ctx.stroke();
        ctx.shadowBlur = 0;
      });
    });
    
  }, [objects, rays, isPlaying]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/science')} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Sun className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">{t.title}</h1>
              <p className="text-xs text-slate-400">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={() => setShowInfo(!showInfo)} className={`p-2 rounded-lg transition-colors ${showInfo ? 'bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'}`}>
              <Info className="w-5 h-5 text-white" />
            </button>
            <button onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
              className="flex items-center gap-1 px-3 py-2 bg-slate-800 rounded-full hover:bg-slate-700 transition-colors">
              <Globe className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-slate-300">{language === 'en' ? 'हिन्दी' : 'ENG'}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 flex flex-col lg:flex-row gap-4">
        {/* Toolbar */}
        <div className="lg:w-64 space-y-4">
          {/* Tools */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Settings className="w-4 h-4" /> {t.tools}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {tools.map(tool => (
                  <button key={tool.id} onClick={() => addObject(tool.id)}
                    className={`p-3 rounded-lg border transition-all text-center ${
                      selectedTool === tool.id 
                        ? 'border-blue-500 bg-blue-500/20' 
                        : 'border-slate-600 bg-slate-700/50 hover:bg-slate-700'
                    }`}>
                    <div className="text-2xl mb-1" style={{ color: tool.color }}>{tool.icon}</div>
                    <div className="text-xs text-slate-300">{tool.name}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Presets */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-300 mb-3">{t.presets}</h3>
              <div className="space-y-2">
                {presets.map(p => (
                  <button key={p.id} onClick={() => loadPreset(p.id)}
                    className={`w-full p-3 rounded-lg border flex items-center gap-3 transition-all ${
                      preset === p.id 
                        ? 'border-green-500 bg-green-500/20' 
                        : 'border-slate-600 bg-slate-700/50 hover:bg-slate-700'
                    }`}>
                    <span className="text-2xl">{p.icon}</span>
                    <span className="text-sm text-slate-300">{p.name}</span>
                    <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Controls */}
          <div className="flex gap-2">
            <Button onClick={() => { setObjects([]); setRays([]); setPreset(null); }} 
              variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700">
              <Trash2 className="w-4 h-4 mr-2" /> {t.clear}
            </Button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1">
          {showInfo && (
            <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500/30 rounded-xl">
              <p className="text-sm text-blue-200">{t.infoText}</p>
            </div>
          )}
          
          <div className="relative bg-slate-900 rounded-2xl overflow-hidden border border-slate-700 shadow-2xl">
            <canvas
              ref={canvasRef}
              width={800}
              height={500}
              className="w-full cursor-move"
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            />
            
            {/* Legend */}
            <div className="absolute bottom-4 right-4 bg-slate-800/90 rounded-lg p-3 text-xs text-slate-400">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <span>{language === 'en' ? 'Focal Point' : 'फोकस बिंदु'}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-slate-500" style={{ borderTop: '2px dashed #475569' }}></div>
                <span>{language === 'en' ? 'Principal Axis' : 'मुख्य अक्ष'}</span>
              </div>
            </div>
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-3">{t.dragToMove}</p>
        </div>
      </div>
    </div>
  );
};

export default LightBenderSimulation;
